import { createDictationSlice } from '@/features/dictation/dictationBubble/store/createDictationSlice';
import { createRecordingSlice } from '@/features/dictation/dictationBubble/store/createRecordingSlice';
import { createSoundsSlice } from '@/features/dictation/dictationBubble/store/createSoundsSlice';
import { setDictationIdReader } from '@/features/dictation/dictationBubble/store/dictationLogger';
import {
  selectUniversalDictationShouldBlockRefresh,
  setUniversalDictationShouldBlockRefresh,
  type DictationStore,
} from '@heidi/dictate';
import { create } from 'zustand';

export const useDictationStore = create<DictationStore>((...args) => ({
  ...createDictationSlice(...args),
  ...createRecordingSlice(...args),
  ...createSoundsSlice(...args),
}));

// Keep a bundle-light refresh guard synchronized for safety checks outside the
// universal-dictation feature graph. The store's initial state is inactive and
// matches the guard's default, so the subscription alone keeps them in sync.
useDictationStore.subscribe((state) => {
  setUniversalDictationShouldBlockRefresh(
    selectUniversalDictationShouldBlockRefresh(state)
  );
});

// SCR-4118: register the store-backed reader once the store is constructed
// so `dictationLogger` can auto-enrich every payload with the current
// session's correlation IDs. Lives here (rather than in dictationLogger.ts)
// to avoid the circular import that would otherwise pull useDictationStore
// into every slice's module-init path. See [[dictationLogger.ts]] for why.
setDictationIdReader(() => {
  const state = useDictationStore.getState();
  return {
    dictationId: state.dictationId,
    dictationRecordingId: state.dictationRecordingId,
  };
});

/** Gets current dictation mode from store. */
export const getDictationMode = () => useDictationStore.getState().dictationMode;

/** Gets current dictation status from store. */
export const getDictationStatus = () => useDictationStore.getState().status;
